import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {Country, EventItem, League, Lookups, Request, RequestTypes} from "@model";
import {ActivatedRoute, Router} from "@angular/router";
import {LEAGUES_EDIT, ORGANISER_EVENT_EDIT, ORGANISER_EVENTS_MANAGEMENT} from "../../app-routing.module";
import {EventService} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {ListOfRequestsType} from "@pages";

@Component({
  selector: 'app-request-details-modal',
  templateUrl: './request-details-modal.component.html',
  styleUrl: './request-details-modal.component.scss',
  standalone: false
})
export class RequestDetailsModalComponent implements OnInit{

  @Input() actionsAllowed: boolean = false;
  @Input() isAdmin: boolean = false;
  @Input({required: true})  request!: Request;
  @Input() languages: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input() listOfRequestsType: ListOfRequestsType = ListOfRequestsType.INCOMING;
  showEventDetailsButton: boolean = false;
  showLeagueDetailsButton: boolean = false;
  events: EventItem[] = [];
  league: League | undefined;
  eventCountry: Map<number, Country> = new Map<number, Country>();
  eventContentOpen: Map<number, boolean> = new Map<number, boolean>();
  lengths: Map<number, (string | undefined)[]> = new Map<number, string[]>();
  listSizeBreakpoint = 3;


  constructor(public activeModal: NgbActiveModal, private router: Router, private route: ActivatedRoute, private eventService: EventService,
              public translocoService: TranslocoService) {
  }

  ngOnInit() {
    this.setRequestData();
    this.setButtonNavigation();
  }

  setRequestData() {
    this.events = [];

    if (this.request.type === RequestTypes.PUBLISH_EVENT || this.request.type === RequestTypes.ADD_EVENT_TO_LEAGUE) {
      if (this.request.event) {
        this.events.push(this.request.event);
      }
      if (this.request.type === RequestTypes.ADD_EVENT_TO_LEAGUE) {
        this.league = this.request.league;
      }
    }
    if (this.request.type === RequestTypes.PUBLISH_LEAGUE) {
      this.league = this.request.league;
    }
    this.events.forEach((event: EventItem) => {
      if (event.id) {
        const country: Country | undefined = this.getEventCountry(event);
        if (country) {
          this.eventCountry.set(event.id!, country)
        }
        this.lengths.set(event.id!, this.getEventLengths(event))
        this.eventContentOpen.set(event.id!, this.request.type === RequestTypes.ADD_EVENT_TO_LEAGUE ? true : false);
      }
    }
    )
  }

  onAccept(): void {
    this.activeModal.close(true);
  }

  onDecline(): void {
    this.activeModal.close(false);
  }

  toggleCollapse(eventId: number) {
    this.eventContentOpen.set(eventId, !this.eventContentOpen.get(eventId));
  }

  onDismissClick(): void {
    /// https://github.com/ng-bootstrap/ng-bootstrap/issues/3721
    this.activeModal.dismiss();
  }

  setButtonNavigation() {
    if (this.isAdmin) {
      this.showEventDetailsButton = true;
      this.showLeagueDetailsButton = true;
    }
    else {
      const requestType : RequestTypes = this.request!.type!;
      switch (requestType) {
        case RequestTypes.ADD_EVENT_TO_LEAGUE:
          this.showEventDetailsButton = this.listOfRequestsType === ListOfRequestsType.INCOMING;
          this.showLeagueDetailsButton = this.listOfRequestsType === ListOfRequestsType.OUTGOING;
          break;
        case RequestTypes.PUBLISH_EVENT:
          this.showEventDetailsButton = true;
          this.showLeagueDetailsButton = false;
          break;
       case RequestTypes.PUBLISH_LEAGUE:
          this.showEventDetailsButton = false;
          this.showLeagueDetailsButton = true;
          break;
      }
    }
  }


  openEventDetailsInNewTab(event: EventItem) {
    if (!this.showEventDetailsButton) return;

    const request = this.request;
    if (!event?.id || !request.type) return;

    const { id: eventId } = event;
    const requestType = request.type!;

    const url = this.router.serializeUrl(
        this.router.createUrlTree([(requestType === RequestTypes.PUBLISH_EVENT) ? ORGANISER_EVENT_EDIT : ORGANISER_EVENTS_MANAGEMENT ], {
          queryParams: { eventId }
        })
    );

    window.open(url, '_blank');
  }


  openLeagueDetailsInNewTab(league: League) {
    if (!this.showLeagueDetailsButton) return;

    const request = this.request;
    if (!league?.id) return;

    const { id: leagueId } = league;

    const url = this.router.serializeUrl(
        this.router.createUrlTree([LEAGUES_EDIT], {
          queryParams: { leagueId }
        })
    );

    window.open(url, '_blank');
  }

  getEventCountry(event: EventItem): Country | undefined {
    if (!this.countries?.length || !event?.country) {
      return undefined;
    }

    return this.countries.find(c => c.id === event.country);
  }

  getEventLengths(event: EventItem): string[] {
    if (!event) {
      return [];
    }

    if (event.lengths?.length) {
      return event.lengths
          .filter((l) => !!l)
          .sort((a, b) => +a - +b)
          .slice(0, this.listSizeBreakpoint)
          .map(String);
    }

    return Array.from(
        new Set(
            (event.racesList ?? [])
                .map(race => race.length)
                .filter((l): l is number => !!l)
                .sort((a, b) => a - b)
                .map(String)
        )
    ).slice(0, this.listSizeBreakpoint);
  }

  protected readonly RequestTypes = RequestTypes;
  protected readonly ListOfRequestsType = ListOfRequestsType;
}
